import { useCallback, useState } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import { AxiosError, isAxiosError } from 'axios';
import { FallbackProps } from 'react-error-boundary';
import { GlobalError } from '@antimatterhq/ui/components/globalError';
import { Skeleton } from '@antimatterhq/ui/components/skeleton';
import { Text } from '@antimatterhq/ui/components/typography';
import { TransformForm } from '../../_partials/TransformForm';
import { useUpdateTransform } from '@/hooks/core/useUpdateTransform';
import { TransformSchemaValues } from '@/zod/schemas/transforms/TransformSchema';
import { setTransformFromValues } from '@/lib/transform/transformHelpers';
import { useGetTransformSuspense } from '@/hooks/core/useGetTransform';
import { SuspenseErrorBoundary } from '@/components/errorBoundary/SuspenseErrorBoundary';
import { TwoColumnLayout } from '@/components/container';
import { bumpVersion } from '@/lib/datasource';
import { useGetTransformsInfinite } from '@/hooks/core/useGetTransformsInfinite';
const EditTransformForm = () => {
  const navigate = useNavigate();
  const { mutateAsync: create, isPending: isSubmitting } = useUpdateTransform();
  const { optimisticUpdate } = useGetTransformsInfinite();
  const [responseError, setResponseError] = useState<
    AxiosError<{ message: string }> | undefined
  >(undefined);
  const match = useMatch('/configure/transforms/edit/:name');
  const { name = '' } = match?.params || {};

  const { data } = useGetTransformSuspense({
    name,
  });

  const onSubmit = useCallback(
    async (values: TransformSchemaValues) => {
      const transform = bumpVersion(setTransformFromValues(values, data));
      await create(transform, {
        toasts: {
          success: 'Transform updated successfully',
          error: 'Failed to update transform',
        },
        onSuccess: async () => {
          optimisticUpdate(transform);
          navigate('/configure/transforms');
        },
        onError: async ({ error }) => {
          setResponseError(error as AxiosError<{ message: string }>);
        },
      });
    },
    [create, navigate, data, optimisticUpdate],
  );

  return (
    <TransformForm
      transform={data}
      responseError={responseError}
      onSubmit={onSubmit}
      isSubmitting={isSubmitting}
      isInEditMode={true}
    />
  );
};

const SuspenseFallback = () => {
  return (
    <TwoColumnLayout.Root>
      <TwoColumnLayout.LeftColumn>
        <div className="flex flex-col gap-4">
          <Skeleton className="h-8 w-56" />
          <Skeleton className="h-8 w-56" />

          <Skeleton className="h-6 w-20" />
          <Skeleton className="h-10 w-full" />

          <Skeleton className="h-6 w-20" />
          <div className="flex gap-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>

          <Skeleton className="h-6 w-20" />
          <Skeleton className="h-10 w-full" />

          <Skeleton className="h-6 w-20" />
          <div className="flex gap-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
          <Skeleton className="h-10 w-20" />

          <Skeleton className="h-6 w-20" />
          <Skeleton className="h-10 w-full" />
        </div>
      </TwoColumnLayout.LeftColumn>
      <TwoColumnLayout.RightColumn>
        <nav className="sticky top-[40px] flex flex-col gap-4">
          <Text size="base" bold>
            Table of contents
          </Text>
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
        </nav>
      </TwoColumnLayout.RightColumn>
    </TwoColumnLayout.Root>
  );
};

const ErrorBoundaryFallback = ({
  resetErrorBoundary,
  error,
}: FallbackProps) => {
  const match = useMatch('/configure/transforms/edit/:name');
  const { name = '' } = match?.params || {};
  const isNotFoundError = isAxiosError(error) && error.response?.status === 404;

  if (isNotFoundError)
    return (
      <GlobalError
        title="Transform not found"
        description={`Transform "${name}" could not be found`}
        onRetry={async () => resetErrorBoundary()}
        learnMoreLink="/configure/transforms"
        learnMoreText="Go to transforms"
        retryText="Retry"
        className="h-[75vh]"
      />
    );

  const errorMessage = isAxiosError(error)
    ? (error.response?.statusText ?? error.message)
    : error.message;

  return (
    <GlobalError
      title="Error loading transform"
      description={errorMessage}
      onRetry={async () => resetErrorBoundary()}
      retryText="Retry"
      className="h-[75vh]"
    />
  );
};

export default function EditTransform() {
  return (
    <SuspenseErrorBoundary
      suspenseFallback={<SuspenseFallback />}
      errorBoundaryFallback={ErrorBoundaryFallback}
    >
      <EditTransformForm />
    </SuspenseErrorBoundary>
  );
}
